import { Sparkles, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { QuickAddResult } from "@shared/types";
import { t } from "@/i18n";
import { post } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button, cx } from "./ui";

const EXAMPLES: Record<"place" | "item", string[]> = {
  place: [t("кафе с яичным кофе в Старом квартале Ханоя"), t("ссылка из Google Maps"), t("ссылка на отель или статью")],
  item: [
    t("ужин в Pho Thin 8 апреля в 19:00"),
    t("ночной автобус Ханой → Сапа 13.04 в 21:00, бронь ABC123, 450 000 ₫"),
    t("3 ночи в Хойане с 20 апреля, отель Little Riverside"),
  ],
};

/** «Описать словами или вставить ссылку» → ИИ заполняет форму, пользователь проверяет и сохраняет */
export function SmartFill({
  tripId,
  want,
  day,
  initial,
  autoRun,
  onResult,
}: {
  tripId: string;
  want: "place" | "item";
  day?: string | null;
  initial?: string;
  autoRun?: boolean;
  onResult: (r: QuickAddResult) => void;
}) {
  const { config } = useAuth();
  const [text, setText] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  const run = async (input = text) => {
    if (input.trim().length < 2 || busy) return;
    setBusy(true);
    setError(null);
    setWarnings([]);
    try {
      const r = await post<QuickAddResult>(`/api/trips/${tripId}/ai/quick-add`, { text: input.trim(), want, day: day ?? null });
      setWarnings(r.warnings);
      onResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (autoRun && initial && !ran.current) {
      ran.current = true;
      void run(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, initial]);

  if (!config?.aiEnabled) return null;
  return (
    <div className="mb-4 rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent-soft)] p-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-bold text-[var(--accent)]">
        <Sparkles size={15} />
        {t("Своими словами или ссылкой")}
      </div>
      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void run();
            }
          }}
          placeholder={t("Например: {example}", { example: EXAMPLES[want][0] })}
          className="min-h-11 flex-1 resize-none rounded-xl border border-[var(--border)] bg-[var(--surface-solid)] px-3 py-2 text-[14px] outline-none focus:border-[var(--accent)]"
        />
        <Button variant="primary" loading={busy} disabled={text.trim().length < 2} onClick={() => run()} className="h-11">
          {busy ? t("Разбираю…") : t("Заполнить")}
        </Button>
      </div>
      <div className="mt-1.5 text-[11px] text-muted">
        {t("или: {examples}. ИИ заполнит поля ниже — проверьте и сохраните.", { examples: EXAMPLES[want].slice(1).join(" · ") })}
      </div>
      {(warnings.length > 0 || error) && (
        <div className={cx("mt-2 space-y-0.5 text-[12px]", error ? "text-[var(--danger)]" : "text-amber-700 dark:text-amber-300")}>
          {error && <div>{error}</div>}
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <TriangleAlert size={12} className="mt-0.5 shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
